import * as Y from 'yjs'
import { safeJsonParse } from '../../core/database/redis.js'
import { gzip, gunzip } from 'node:zlib'
import { promisify } from 'node:util'
import { createHash } from 'node:crypto'
import type { Firestore } from 'firebase-admin/firestore'
import { FieldValue } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import { createLogger } from '../../common/utils/logger.js'
import { PlannerRepository } from '../planner/planner.repository.js'
import type { Stage } from '@my-roadtrip/types'
import { isString, isNonEmptyString } from '../../common/utils/type-guards.js'


const logger = createLogger('CollabService')
const gzipAsync = promisify(gzip)
const gunzipAsync = promisify(gunzip)

class SnapshotPersistenceError extends Error {
  constructor(
    readonly planId: string,
    cause?: unknown
  ) {
    super(`Failed to persist snapshot for plan ${planId}`)
    this.name = 'SnapshotPersistenceError'
    if (cause !== undefined) {
      ; (this as { cause?: unknown }).cause = cause
    }
  }
}

export class CollabService {
  private readonly compressionEnabled = process.env.ENABLE_YDOC_COMPRESSION !== 'false'
  private readonly snapshotThrottleSeconds = 60
  private readonly snapshotHashTtlSeconds = 24 * 60 * 60
  private readonly planAccessCacheTtlSeconds = 60
  private readonly plannerRepository: PlannerRepository

  constructor(
    private readonly redis: RedisClient,
    private readonly firestore: Firestore
  ) {
    this.plannerRepository = new PlannerRepository(firestore)
  }

  private planAccessCacheKey(planId: string): string {
    return `plan:access:${planId}`
  }

  /**
   * Get plan document from Firestore for access validation
   */
  async getPlanDocument(planId: string) {
    return await this.firestore.collection('plans').doc(planId).get()
  }

  async getPlanAccess(planId: string): Promise<{
    ownerId: string | null
    isPublic: boolean
    collaborators: Array<{ userId: string; role: string }>
  } | null> {
    const cacheKey = this.planAccessCacheKey(planId)

    try {
      const cached = await this.redis.get(cacheKey)
      if (cached) {
        const parsed = safeJsonParse<{
          ownerId: string | null
          isPublic: boolean
          collaborators: Array<{ userId: string; role: string }>
        }>(cached, cacheKey)

        if (parsed) {
          return parsed
        }
        // safeJsonParse already logs error and tries to delete key
      }
    } catch (error) {
      logger.warn('Failed to read plan access cache', { planId, error })
    }

    const doc = await this.getPlanDocument(planId)
    if (!doc.exists) {
      return null
    }

    const data = doc.data() as {
      userId?: string
      isPublic?: boolean
      collaborators?: Array<{ userId?: string; role?: string }>
    }

    const collaborators =
      data?.collaborators
        ?.map(collaborator => {
          const userId = typeof collaborator?.userId === 'string' ? collaborator.userId : null
          const role = typeof collaborator?.role === 'string' ? collaborator.role : 'viewer'
          if (!userId) return null
          return { userId, role }
        })
        .filter(Boolean) ?? []

    const normalized = {
      ownerId: typeof data?.userId === 'string' ? data.userId : null,
      isPublic: Boolean(data?.isPublic),
      collaborators: collaborators as Array<{ userId: string; role: string }>
    }

    try {
      await this.redis.set(cacheKey, JSON.stringify(normalized), { ex: this.planAccessCacheTtlSeconds })
    } catch (error) {
      logger.warn('Failed to cache plan access info', { planId, error })
    }

    return normalized
  }

  async invalidatePlanAccess(planId: string): Promise<void> {
    try {
      await this.redis.del(this.planAccessCacheKey(planId))
    } catch (error) {
      logger.warn('Failed to invalidate plan access cache', { planId, error })
    }
  }

  async syncPlan(
    planId: string,
    userId: string,
    deviceId: string,
    clientUpdate: Buffer,
    ownerId?: string | null
  ): Promise<Buffer> {
    logger.debug('Syncing plan CRDT', { planId, userId, deviceId, updateSize: clientUpdate.length })

    // Load server Y.Doc from Redis (with decompression)
    const serverDoc = new Y.Doc()
    const cachedState = await this.redis.getBuffer(`ydoc:${planId}`)

    logger.debug('CRDT state retrieved from Redis', { planId, cachedStateSize: cachedState?.length || 0 })

    if (cachedState) {
      let stateBuffer = cachedState

      // Decompress if compression is enabled
      if (this.compressionEnabled && cachedState.length > 0) {
        try {
          stateBuffer = await gunzipAsync(cachedState)
          logger.debug('CRDT state decompressed', { planId, compressed: cachedState.length, decompressed: stateBuffer.length })
        } catch (err) {
          // SECURITY FIX (ISSUE-004): Never silently apply corrupted data
          // Decompression failure indicates state corruption - throw to force fresh sync
          logger.error('CRITICAL: Failed to decompress Y.Doc state - state corruption detected', {
            planId,
            error: err,
            bufferSize: cachedState.length
          })
          throw new Error(`CRDT state corrupted for plan ${planId}: decompression failed`)
        }
      }

      Y.applyUpdate(serverDoc, new Uint8Array(stateBuffer))
    }

    logger.debug('Applying client update to CRDT', { planId, userId, updateSize: clientUpdate.length })
    // Apply client updates (CRDT merge)
    Y.applyUpdate(serverDoc, new Uint8Array(clientUpdate))

    // Save merged state to Redis (with compression)
    const mergedState = Y.encodeStateAsUpdate(serverDoc)
    let stateToStore = Buffer.from(mergedState)

    if (this.compressionEnabled) {
      try {
        stateToStore = await gzipAsync(mergedState)
        // Log compression ratio for monitoring
        const ratio = ((1 - stateToStore.length / mergedState.length) * 100).toFixed(1)
        logger.debug('Y.Doc state compressed', { originalSize: mergedState.length, compressedSize: stateToStore.length, ratio })
      } catch (err) {
        logger.warn('Failed to compress Y.Doc state, storing uncompressed', { error: err })
        // Fallback to raw if compression fails
      }
    }

    await this.redis.set(`ydoc:${planId}`, stateToStore)

    const snapshotDecision = await this.shouldPersistSnapshot(planId, mergedState)

    if (snapshotDecision.persist) {
      try {
        await this.persistSnapshot(planId, serverDoc, userId, deviceId, snapshotDecision.stateHash)
        await this.markSnapshotStored(planId, snapshotDecision.stateHash)
      } catch (error) {
        await this.redis.del(this.snapshotThrottleKey(planId))
        throw error
      }
    } else {
      logger.debug('Skipping snapshot persistence', {
        planId,
        reason: snapshotDecision.reason
      })
    }

    // Persist canonical plan data whenever the CRDT state changed, even if snapshot throttled
    if (snapshotDecision.reason !== 'unchanged-state') {
      await this.persistStagesFromDoc(planId, serverDoc)
      await this.invalidatePlannerCache(planId, ownerId)
    }

    return Buffer.from(mergedState)
  }

  private parseDate(value: unknown): Date | undefined {
    if (!value) return undefined
    if (value instanceof Date) return value
    if (isString(value) || typeof value === 'number') {
      const parsed = new Date(value)
      if (!Number.isNaN(parsed.getTime())) {
        return parsed
      }
    }
    return undefined
  }

  private normalizeStageFromDoc(planId: string, stageId: string, raw: unknown): Stage | null {
    if (!raw || typeof raw !== 'object') {
      return null
    }

    const payload = raw as Record<string, unknown>
    const name =
      isNonEmptyString(payload.name)
        ? payload.name.trim()
        : 'Étape'

    const order = typeof payload.order === 'number' ? payload.order : 0
    const location =
      (payload.location as Stage['location']) ?? {
        address: '',
        coordinates: { lat: Number.NaN, lng: Number.NaN },
        lat: Number.NaN,
        lng: Number.NaN
      }

    return {
      id: stageId,
      planId,
      name,
      order,
      type: (payload.type as Stage['type']) ?? 'waypoint',
      location: location as Stage['location'],
      duration: typeof payload.duration === 'number' ? payload.duration : undefined,
      notes: typeof payload.notes === 'string' ? payload.notes : undefined,
      estimatedArrival: this.parseDate(payload.estimatedArrival) ?? null,
      estimatedDeparture: this.parseDate(payload.estimatedDeparture) ?? null,
      actualArrival: this.parseDate((payload as { actualArrival?: unknown }).actualArrival) ?? null,
      actualDeparture: this.parseDate((payload as { actualDeparture?: unknown }).actualDeparture) ?? null,
      completedAt: this.parseDate(payload.completedAt) ?? null,
      pois: Array.isArray(payload.pois) ? (payload.pois as Stage['pois']) : undefined,
      routeData: payload.routeData as Stage['routeData'],
      photos: Array.isArray((payload as { photos?: unknown }).photos)
        ? ((payload as { photos: unknown[] }).photos as Stage['photos'])
        : undefined,
      weather: payload.weather as Stage['weather'],
      updatedAt: this.parseDate(payload.updatedAt) ?? undefined,
      createdAt: this.parseDate(payload.createdAt) ?? undefined
    }
  }

  private async persistStagesFromDoc(planId: string, doc: Y.Doc): Promise<void> {
    const stagesMap = doc.getMap<any>('stages')
    const stages: Stage[] = []

    for (const [id, value] of stagesMap.entries()) {
      const normalized = this.normalizeStageFromDoc(planId, id, value)
      if (normalized) {
        stages.push(normalized)
      }
    }

    stages.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

    if (stages.length === 0) {
      logger.warn('No stages found in CRDT state during persistence', { planId })
    }

    await this.plannerRepository.upsertStagesFromCollab(planId, stages)
  }

  private async invalidatePlannerCache(planId: string, ownerId?: string | null): Promise<void> {
    const keys = [`plan:${planId}`]
    if (ownerId) {
      keys.push(`plan:user:${ownerId}`, `planner:dashboard:${ownerId}`)
    }

    try {
      await Promise.all(keys.map(key => this.redis.del(key)))
      logger.debug('Planner caches invalidated after collab sync', { planId, ownerId })
    } catch (error) {
      logger.warn('Failed to invalidate planner caches after collab sync', { planId, ownerId, error })
    }
  }

  /**
   * Parse and validate a single presence payload from Redis.
   * Returns the parsed presence entry or null if invalid/expired.
   */
  private parsePresenceEntry(json: string, now: number, planId: string): {
    userId: string
    userName?: string
    userAvatar?: string
    cursor?: { lat: number; lng: number }
    deviceId?: string
    color?: string
    timestamp: number
  } | null {
    try {
      const parsed = JSON.parse(json) as Record<string, unknown>
      const userId = typeof parsed.userId === 'string' && parsed.userId.length > 0 ? parsed.userId : null
      if (!userId) return null

      const timestampValue = typeof parsed.timestamp === 'number' ? parsed.timestamp : Number(parsed.timestamp)
      if (!Number.isFinite(timestampValue)) return null
      if (now - timestampValue >= 30000) return null

      const cursorData = parsed.cursor as { lat?: unknown; lng?: unknown } | undefined
      let cursor: { lat: number; lng: number } | undefined
      if (cursorData) {
        const lat = Number(cursorData.lat)
        const lng = Number(cursorData.lng)
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          cursor = { lat, lng }
        }
      }

      return {
        userId,
        userName: typeof parsed.userName === 'string' ? parsed.userName : undefined,
        userAvatar: typeof parsed.userAvatar === 'string' ? parsed.userAvatar : undefined,
        cursor,
        deviceId: typeof parsed.deviceId === 'string' ? parsed.deviceId : undefined,
        color: typeof parsed.color === 'string' ? parsed.color : undefined,
        timestamp: timestampValue
      }
    } catch (err) {
      logger.warn('Skipping invalid presence payload', { planId, error: err })
      return null
    }
  }

  async getPresence(planId: string): Promise<Array<{
    userId: string
    userName?: string
    userAvatar?: string
    cursor?: { lat: number; lng: number }
    deviceId?: string
    color?: string
    timestamp: number
  }>> {
    const key = `presence:${planId}`
    let data: Record<string, string>

    try {
      data = await this.redis.hgetall(key)
    } catch (err) {
      logger.warn('Failed to load presence state from Redis', { planId, error: err })
      return []
    }

    if (!data || Object.keys(data).length === 0) {
      return []
    }

    const now = Date.now()
    const presence: Array<{
      userId: string
      userName?: string
      userAvatar?: string
      cursor?: { lat: number; lng: number }
      deviceId?: string
      color?: string
      timestamp: number
    }> = []

    for (const json of Object.values(data)) {
      if (!json) continue

      const entry = this.parsePresenceEntry(json, now, planId)
      if (entry) {
        presence.push(entry)
      }
    }

    return presence
  }

  async updatePresence(
    planId: string,
    userId: string,
    data: { cursor?: { lat: number; lng: number }; deviceId: string; timestamp: number }
  ): Promise<void> {
    const key = `presence:${planId}`

    // Get user info (cached)
    const userInfo = await this.getUserInfo(userId)

    await this.redis.hset(
      key,
      userId,
      JSON.stringify({
        userId,
        userName: userInfo.displayName,
        userAvatar: userInfo.photoURL,
        cursor: data.cursor,
        deviceId: data.deviceId,
        timestamp: data.timestamp,
        color: this.getUserColor(userId)
      })
    )

    // Expire after 30s
    await this.redis.expire(key, 30)
    logger.debug('Presence state persisted', {
      planId,
      userId,
      hasCursor: Boolean(data.cursor),
      deviceId: data.deviceId
    })
  }

  private async persistSnapshot(
    planId: string,
    doc: Y.Doc,
    userId: string,
    deviceId: string,
    stateHash: string
  ): Promise<void> {
    try {
      // Convert Y.Doc to JSON
      const stages = doc.getMap('stages').toJSON()
      const stateVector = Y.encodeStateVector(doc)

      await this.firestore
        .collection('plans')
        .doc(planId)
        .collection('snapshots')
        .add({
          stages,
          version: stateHash,
          stateVector: Buffer.from(stateVector).toString('base64'),
          stateHash,
          userId,
          deviceId,
          createdAt: FieldValue.serverTimestamp()
        })
      logger.info('CRDT snapshot persisted', {
        planId,
        userId,
        deviceId,
        version: stateHash,
        stagesCount: typeof stages === 'object' ? Object.keys(stages).length : 0
      })
    } catch (err) {
      await this.recordSnapshotFailure(planId, userId, deviceId, err)
      throw new SnapshotPersistenceError(planId, err)
    }
  }

  private async getUserInfo(userId: string): Promise<{ displayName: string; photoURL?: string }> {
    const cacheKey = `user:${userId}:info`
    const cached = await this.redis.get(cacheKey)
    if (cached) {
      const parsed = safeJsonParse<{ displayName: string; photoURL?: string }>(cached, cacheKey)
      if (parsed) {
        logger.debug('User info cache hit', { userId })
        return parsed
      }
      logger.warn('Failed to parse cached user info, safeJsonParse handled cleanup', { userId })
    }

    const userDoc = await this.firestore.collection('users').doc(userId).get()
    logger.debug('User info fetched from Firestore', { userId, exists: userDoc.exists })
    const info = userDoc.data() || { displayName: 'Anonymous' }

    await this.redis.setex(cacheKey, 3600, JSON.stringify(info))
    logger.debug('User info cached', { userId, ttl: 3600 })
    return info as { displayName: string; photoURL?: string }
  }

  private getUserColor(userId: string): string {
    const colors = ['#E53935', '#1E88E5', '#43A047', '#FB8C00', '#8E24AA']
    let hash = 0
    for (let i = 0; i < userId.length; i++) {
      hash += userId.codePointAt(i) || 0
    }
    return colors[hash % colors.length]
  }

  private async recordSnapshotFailure(
    planId: string,
    userId: string,
    deviceId: string,
    error: unknown
  ): Promise<void> {
    try {
      const payload = {
        planId,
        userId,
        deviceId,
        error: this.serializeError(error),
        timestamp: Date.now()
      }

      await this.redis.lpush('collab:snapshot:failures', JSON.stringify(payload))
      await this.redis.ltrim('collab:snapshot:failures', 0, 49)
    } catch (recordError) {
      logger.warn('Failed to record CRDT snapshot failure', { planId, recordError })
    }
  }

  private serializeError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack
      }
    }

    if (typeof error === 'object' && error !== null) {
      return {
        ...error
      } as Record<string, unknown>
    }

    return {
      message: typeof error === 'string' ? error : 'Unknown error'
    }
  }

  private snapshotThrottleKey(planId: string): string {
    return `collab:snapshot:throttle:${planId}`
  }

  private snapshotHashKey(planId: string): string {
    return `collab:snapshot:hash:${planId}`
  }

  private hashState(state: Uint8Array): string {
    return createHash('sha1').update(state).digest('hex')
  }

  private async shouldPersistSnapshot(
    planId: string,
    mergedState: Uint8Array
  ): Promise<{ persist: boolean; stateHash: string; reason?: string }> {
    const stateHash = this.hashState(mergedState)
    const hashKey = this.snapshotHashKey(planId)
    const previousHash = await this.redis.get(hashKey)

    if (previousHash === stateHash) {
      logger.debug('Snapshot unchanged, skipping persistence', { planId, stateHash })
      return { persist: false, stateHash, reason: 'unchanged-state' }
    }

    const throttleKey = this.snapshotThrottleKey(planId)
    const throttleValue = await this.redis.incr(throttleKey)

    if (throttleValue === 1) {
      await this.redis.expire(throttleKey, this.snapshotThrottleSeconds)
      logger.debug('Snapshot persistence allowed', { planId, stateHash })
      return { persist: true, stateHash }
    }

    await this.redis.expire(throttleKey, this.snapshotThrottleSeconds)
    logger.debug('Snapshot persistence throttled', { planId, stateHash })
    return { persist: false, stateHash, reason: 'throttled' }
  }

  private async markSnapshotStored(planId: string, stateHash: string): Promise<void> {
    const hashKey = this.snapshotHashKey(planId)
    await this.redis.setex(hashKey, this.snapshotHashTtlSeconds, stateHash)
    logger.debug('Snapshot hash cached', { planId, stateHash })
  }
}
